import { useContext, useEffect, useRef } from 'react';

import useOfflineSync from './use-offline-sync';
import { useBoxStatusesQuery, useCourierCacheInvalidation, useWarehouseIncomingInvalidation, useWarehouseOutgoingInvalidation } from './use-queries';

import AuthContext from '../store/auth-context';
import AppContext from '../store/app-context';

/**
 * Shared layout services used by both ScannerLayout and DashboardLayout.
 * Handles offline queue sync, cache invalidation on reconnect, and box statuses prefetch.
 */
const useLayoutServices = () => {
    const appCtx = useContext(AppContext);
    const authCtx = useContext(AuthContext);

    const syncingRef = useRef(false);

    const { runSync } = useOfflineSync();
    const { invalidateCourierCache } = useCourierCacheInvalidation();
    const { invalidateWarehouseIncoming } = useWarehouseIncomingInvalidation();
    const { invalidateWarehouseOutgoing } = useWarehouseOutgoingInvalidation();

    // Prefetch box statuses via React Query (cached for any component that needs them)
    useBoxStatusesQuery(authCtx.isAuthenticated);

    // Sync offline queue when authenticated and online.
    // Runs on every offline→online transition (not just first mount).
    // syncingRef prevents concurrent calls, not repeated ones.
    useEffect(() => {
        if (authCtx.isAuthenticated && appCtx.isOnline && !syncingRef.current) {
            syncingRef.current = true;
            runSync().then((result) => {
                if (result && result.processed > 0) {
                    invalidateCourierCache();
                    invalidateWarehouseIncoming();
                    invalidateWarehouseOutgoing();
                }
            }).catch(console.warn)
              .finally(() => { syncingRef.current = false; });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authCtx.isAuthenticated, appCtx.isOnline]);
};

export default useLayoutServices;
